
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { SermonPlan, SermonFormData } from "./types";

export function useSermonForm(
  createSermonPlan: (data: SermonFormData) => Promise<boolean>,
  updateSermonPlan: (id: string, data: SermonFormData) => Promise<boolean>,
  fetchData: () => void
) {
  const { toast } = useToast();
  const [formOpen, setFormOpen] = useState(false);
  const [currentSermonPlan, setCurrentSermonPlan] = useState<SermonPlan | null>(null);
  const [initialFormData, setInitialFormData] = useState<SermonFormData | null>(null);

  const handleOpenForm = (sermonPlanOrData?: SermonPlan | SermonFormData) => {
    if (!sermonPlanOrData) {
      // Opening a blank form
      setCurrentSermonPlan(null);
      setInitialFormData(null);
    } else if ('id' in sermonPlanOrData) {
      // It's a SermonPlan for editing
      setCurrentSermonPlan(sermonPlanOrData);
      setInitialFormData(null);
    } else {
      // It's SermonFormData for creating a new sermon
      setCurrentSermonPlan(null);
      setInitialFormData(sermonPlanOrData);
    }
    setFormOpen(true);
  };

  const handleFormSubmit = async (formData: SermonFormData) => {
    if (!formData.title) {
      toast({
        title: "Error",
        description: "Sermon title is required",
        variant: "destructive",
      });
      return;
    }

    try {
      let success = false;
      
      if (currentSermonPlan) {
        success = await updateSermonPlan(currentSermonPlan.id, formData);
      } else {
        success = await createSermonPlan(formData);
      }

      if (success) {
        setFormOpen(false);
        setCurrentSermonPlan(null);
        setInitialFormData(null);
        fetchData(); // Refresh the data
        
        toast({
          title: "Success",
          description: currentSermonPlan 
            ? "Sermon plan updated successfully" 
            : "New sermon plan created successfully",
        });
      }
    } catch (error: any) {
      console.error("Error saving sermon plan:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save sermon plan",
        variant: "destructive",
      });
    }
  };

  return {
    formOpen,
    currentSermonPlan,
    initialFormData,
    setFormOpen,
    handleOpenForm,
    handleFormSubmit
  };
}
